import type { ReactNode, Ref } from "react";
import { NO_SCRAP_TEXT, scrapOverText } from "./pickup";

type Aria = { "aria-invalid"?: boolean; "aria-describedby"?: string };

/** Rule D-17: the Total and Scrap fields of one pickup, side by side, on each driver entry. */
export function GallonsFields({
  id,
  testId,
  word,
  total,
  scrap,
  onTotal,
  onScrap,
  disabled,
  totalRef,
  totalAria = {},
  scrapAria = {},
  children,
}: {
  id: string;
  testId: string;
  word: string;
  total: string;
  scrap: string;
  onTotal: (text: string) => void;
  onScrap: (text: string) => void;
  disabled?: boolean;
  totalRef?: Ref<HTMLInputElement>;
  totalAria?: Aria;
  scrapAria?: Aria;
  children?: ReactNode;
}) {
  const over = scrapOverText(total, scrap, word);
  const overId = `${id}-over`;
  return (
    <div className="gallons-fields" data-testid={`${testId}-amounts`}>
      <label>
        Total {word}
        <input
          ref={totalRef}
          id={`${id}-gallons`}
          data-testid={`${testId}-gallons`}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0"
          value={total}
          disabled={disabled}
          {...totalAria}
          onChange={(e) => onTotal(e.target.value)}
        />
      </label>
      <label>
        Scrap {word}
        <input
          id={`${id}-scrap`}
          data-testid={`${testId}-scrap`}
          inputMode="decimal"
          autoComplete="off"
          value={scrap}
          disabled={disabled}
          {...(over ? { "aria-invalid": true, "aria-describedby": overId } : scrapAria)}
          onChange={(e) => onScrap(e.target.value)}
        />
      </label>
      {over ? (
        <p id={overId} role="alert" className="error" data-testid={`${testId}-scrap-over`}>
          {over}
        </p>
      ) : (
        children
      )}
    </div>
  );
}

/** The confirm of a pickup with no scrap, in the pattern of the old entry confirm. */
export function NoScrapConfirm({ id, testId, disabled, onYes, onChange }: { id: string; testId: string; disabled?: boolean; onYes: () => void; onChange: () => void }) {
  return (
    <p
      className="confirm"
      role="group"
      aria-label="A pickup with no scrap"
      data-testid={`${testId}-no-scrap-confirm`}
      onKeyDown={(e) => {
        if (e.key === "Escape") onChange();
      }}
    >
      <span aria-hidden="true">⚠ </span>
      {NO_SCRAP_TEXT}{" "}
      <button id={`${id}-no-scrap-yes`} type="button" className="primary" data-testid={`${testId}-no-scrap-yes`} disabled={disabled} onClick={onYes}>
        Yes, no scrap
      </button>{" "}
      <button type="button" data-testid={`${testId}-no-scrap-no`} disabled={disabled} onClick={onChange}>
        Change it
      </button>
    </p>
  );
}
